import ModifiedDropdown from '@wandb/weave/common/components/elements/ModifiedDropdown';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {type DropdownProps} from 'semantic-ui-react';

import {trackSetting} from '../../services/analytics/workspaceSettingsEvents';
import {observeMetricSelectionPerfWindow} from '../../util/datadog/criticalWorkflows/perfWindow/metricSelection';
import {createLazyPickerSearch} from '../../util/pickerSearchIndex';
import {useTrackSettingPage} from '../WorkspaceDrawer/Settings/hooks/useTrackSettingEvent';
import {
  buildYAxisSearchTexts,
  getOrderedYAxisMetricKeys,
  getVisibleYAxisMetricKeys,
  getYAxisDropdownOption,
  getYAxisDropdownOptionsForKeys,
} from './MetricsPickerDropdownOptions';
import {
  type MetricsPickerProps,
  MetricsRegexToggle,
} from './MetricsPickerShared';

const Y_AXIS_DROPDOWN_RESULT_LIMIT = 100;

export const MetricsPickerDropdown = (props: MetricsPickerProps) => {
  const {config, options, keyTypes, updateConfig} = props;
  const trackSettingPage = useTrackSettingPage();

  const [selectedValues, setSelectedValues] = useState(config.metrics);
  const orderedOptions = useMemo(
    () => getOrderedYAxisMetricKeys(options, keyTypes),
    [keyTypes, options]
  );
  const dropdownOptions = useMemo(
    () =>
      getYAxisDropdownOptionsForKeys(
        getVisibleYAxisMetricKeys(
          orderedOptions,
          selectedValues,
          Y_AXIS_DROPDOWN_RESULT_LIMIT
        ),
        keyTypes
      ),
    [keyTypes, orderedOptions, selectedValues]
  );
  // `ModifiedDropdown` runs the search callback over the full ordered key set
  // on every debounced search (400ms trailing — once per typing pause).
  const pickerSearch = useMemo(
    function makeYAxisPickerSearch() {
      return createLazyPickerSearch(function buildYAxisTexts() {
        return buildYAxisSearchTexts(orderedOptions, keyTypes);
      });
    },
    [keyTypes, orderedOptions]
  );
  const searchYAxisOptions = useCallback(
    (_options: any[], query: string) =>
      pickerSearch
        .search(query, Y_AXIS_DROPDOWN_RESULT_LIMIT)
        .map(i => getYAxisDropdownOption(orderedOptions[i], keyTypes)),
    [keyTypes, orderedOptions, pickerSearch]
  );
  const firstDropdownMetric = dropdownOptions[0]?.value;
  const newMetrics = React.useMemo(
    () =>
      firstDropdownMetric == null
        ? undefined
        : getUpdatedDropdownMetrics(
            [firstDropdownMetric],
            k => keyTypes[k] === 'histogram'
          ),
    [firstDropdownMetric, keyTypes]
  );
  /**
   * Whenever we get metrics that don't match selected values, update the selected values
   * this keeps the component synced to whatever the spec is
   */
  useEffect(
    () => {
      if (!stringArraysEqual(selectedValues, config.metrics)) {
        setSelectedValues(config.metrics);
      }
    },
    // we only want to update this when a new value comes in as props
    // selected values will eagerly updated on change
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [config.metrics]
  );

  // Set a nice default metric if possible
  useEffect(() => {
    if (selectedValues == null && newMetrics != null) {
      if (!stringArraysEqual(newMetrics, config.metrics)) {
        updateConfig({
          metrics: newMetrics,
        });
      }
    }
  }, [config.metrics, newMetrics, selectedValues, updateConfig]);

  const onDropdownChange = useCallback(
    (event: React.SyntheticEvent, {value: newValue}: DropdownProps) => {
      if (
        Array.isArray(newValue) &&
        newValue.every(value => typeof value === 'string')
      ) {
        const updatedMetrics = getUpdatedDropdownMetrics(
          newValue as string[],
          k => keyTypes[k] === 'histogram'
        );
        observeMetricSelectionPerfWindow({
          axis: 'y',
          event,
          initialSelection: selectedValues ?? [],
          nextSelection: updatedMetrics,
          pickerVariant: 'history_keys',
        });
        setSelectedValues(updatedMetrics);
        updateConfig({
          metrics: updatedMetrics,
        });

        trackSetting({
          action: 'change',
          page: trackSettingPage,
          settingLevel: 'panel',
          setting: 'yAxis',
          settingValue: updatedMetrics.toString(),
          additionalProperties: {
            metrics: updatedMetrics,
          },
        });
      }
    },
    [keyTypes, selectedValues, trackSettingPage, updateConfig]
  );

  return (
    <div className="metrics-picker">
      <div className="metrics-picker-control-wrapper">
        <ModifiedDropdown
          perfAction="edit-panel-y-metric-picker"
          allowRegexSearch
          className="regex-dropdown"
          data-test="y-axis"
          lazyLoad
          multiple
          onChange={onDropdownChange}
          options={dropdownOptions}
          placeholder="Select metrics to visualize"
          resultLimit={Y_AXIS_DROPDOWN_RESULT_LIMIT}
          search={searchYAxisOptions}
          selection
          value={selectedValues}
        />
        <div className="metrics-picker-dropdown-actions">
          <MetricsRegexToggle {...props} />
        </div>
      </div>
    </div>
  );
};

/**
 * Histograms can't coexist with other metrics (including other histograms). If
 * the selection includes one, collapse to just the newest pick (the last
 * element). Callers supply `isHistogram` so the histogram check can come from
 * either the legacy history-key type map or the metric-search API types.
 */
export const getUpdatedDropdownMetrics = (
  newMetrics: string[],
  isHistogram: (key: string) => boolean
): string[] => {
  if (newMetrics.some(isHistogram)) {
    return newMetrics.slice(-1);
  }
  return newMetrics;
};

function stringArraysEqual(
  left: string[] | undefined,
  right: string[] | undefined
) {
  // Metrics are flat string arrays; lodash isEqual was showing up in profiles.
  if (left === right) {
    return true;
  }
  if (left == null || right == null || left.length !== right.length) {
    return false;
  }
  for (let i = 0; i < left.length; i++) {
    if (left[i] !== right[i]) {
      return false;
    }
  }
  return true;
}
